import type { Point3 } from "@/types/blocks.types";

/**
 * Calculate arc center, radius, and angles from 3 points using perpendicular bisectors.
 * Returns null if the points are collinear.
 */
export function calculateArcFrom3Points(
  start: Point3,
  middle: Point3,
  end: Point3,
): {
  center: { x: number; y: number };
  radius: number;
  startAngle: number;
  endAngle: number;
} | null {
  const x1 = start[0],
    y1 = start[1];
  const x2 = middle[0],
    y2 = middle[1];
  const x3 = end[0],
    y3 = end[1];

  const A = x2 - x1;
  const B = y2 - y1;
  const C = x3 - x1;
  const D = y3 - y1;
  const E = A * (x1 + x2) + B * (y1 + y2);
  const F = C * (x1 + x3) + D * (y1 + y3);
  const G = 2 * (A * (y3 - y2) - B * (x3 - x2));

  if (Math.abs(G) < 1e-10) {
    return null;
  }

  const cx = (D * E - B * F) / G;
  const cy = (A * F - C * E) / G;
  const radius = Math.sqrt((cx - x1) ** 2 + (cy - y1) ** 2);

  const startAngle = Math.atan2(y1 - cy, x1 - cx);
  const middleAngle = Math.atan2(y2 - cy, x2 - cx);
  const endAngle = Math.atan2(y3 - cy, x3 - cx);

  const normalizedStart =
    startAngle < 0 ? startAngle + 2 * Math.PI : startAngle;
  const normalizedMiddle =
    middleAngle < 0 ? middleAngle + 2 * Math.PI : middleAngle;
  const normalizedEnd = endAngle < 0 ? endAngle + 2 * Math.PI : endAngle;

  const ccwToEnd =
    normalizedEnd >= normalizedStart
      ? normalizedEnd - normalizedStart
      : normalizedEnd + 2 * Math.PI - normalizedStart;
  const ccwToMiddle =
    normalizedMiddle >= normalizedStart
      ? normalizedMiddle - normalizedStart
      : normalizedMiddle + 2 * Math.PI - normalizedStart;

  if (ccwToMiddle > ccwToEnd) {
    return {
      center: { x: cx, y: cy },
      radius,
      startAngle: endAngle,
      endAngle: startAngle,
    };
  }

  return { center: { x: cx, y: cy }, radius, startAngle, endAngle };
}
